"use client";

import { cn } from "@v1/ui/utils";
import { X } from "lucide-react";
import { useTranslations } from "next-intl";
import { type ReactElement, useEffect } from "react";
import { ChatPausedIllustration } from "./chat-paused-illustration";
import { MESSENGER_ICON_BUTTON_CLASS } from "./messenger-header";

/** What visitors see while the chat's monthly cap is reached: no reason, just a pause. */
export function ChatPausedNotice({ className }: { readonly className?: string }): ReactElement {
  const t = useTranslations("messenger.paused");
  return (
    <div className={cn("flex flex-col items-center justify-center px-8 text-center", className)}>
      <ChatPausedIllustration />
      <h2 className="mt-5 text-[15px] font-semibold text-primary/90">{t("titel")}</h2>
      <p className="mt-1.5 text-[13px] text-primary/55">{t("text")}</p>
    </div>
  );
}

/** Small window above the bubble; phones get the full screen. */
export function MessengerPausedPopover({ onClose }: { readonly onClose: () => void }): ReactElement {
  const t = useTranslations("messenger.header");
  const tPaused = useTranslations("messenger.paused");
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      role="dialog"
      aria-label={tPaused("titel")}
      className={cn(
        "fixed inset-0 z-[1050] flex flex-col overflow-hidden bg-background print:hidden",
        "sm:inset-auto sm:bottom-[5.5rem] sm:right-5 sm:w-[360px] sm:rounded-2xl sm:shadow-[0_12px_48px_rgba(0,0,0,0.18)] sm:ring-1 sm:ring-black/[0.06]",
        "origin-bottom-right animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2 duration-200",
      )}
    >
      <div className="flex h-12 shrink-0 items-center justify-end px-3">
        <button type="button" aria-label={t("schliessen")} onClick={onClose} className={MESSENGER_ICON_BUTTON_CLASS}>
          <X className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
        </button>
      </div>
      <ChatPausedNotice className="flex-1 pb-12 pt-2" />
    </div>
  );
}
